import React, { useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import { useAuth0 } from "@auth0/auth0-react";

// react-bootstrap components
import {
  Badge,
  Button,
  ButtonGroup,
  Card,
  Collapse,
  Form,
  InputGroup,
  Navbar,
  Nav,
  Pagination,
  Container,
  Row,
  Col
} from "react-bootstrap";
import SitewideDataContext from "../../utils/sitewideContext";

function Sidebar({ routes, image, background }) {
  const { user, logout } = useAuth0();
  const location = useLocation();
  const { sitewideData } = useContext(SitewideDataContext);

  // Admin: backend userInfo.admin / is_admin / role, or Auth0 app_metadata / custom claim
  // Optional: ?showAdmin=1 in URL forces admin menu (for testing)
  const queryShowAdmin =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("showAdmin") === "1";
  const userInfo = sitewideData?.userInfo;
  const isAdmin =
    queryShowAdmin ||
    userInfo?.admin === true ||
    userInfo?.is_admin === true ||
    userInfo?.role === "CEO" ||
    user?.app_metadata?.role === "admin" ||
    user?.["https://api.watchmypage.com/role"] === "admin";

  const createLinks = (routeList) => {
    return routeList.map((prop, key) => {
      if (prop.redirect || prop.hidden) return null;
      const path = prop.layout + prop.path;
      const isActive = activeRoute(path) === "active";
      return (
        <Nav.Item
          className={activeRoute(path)}
          key={key}
          as="li"
        >
          <Nav.Link
            to={path}
            as={Link}
            aria-current={isActive ? "page" : undefined}
          >
            <i className={prop.icon} aria-hidden="true" />
            <p>{prop.name}</p>
          </Nav.Link>
        </Nav.Item>
      );
    });
  };

  const debugActive =
    typeof window !== "undefined" && window.location.href.includes("debug");
  // verifies if routeName is the one active (in browser input)
  const activeRoute = (routeName) => {
    return location.pathname === routeName ? "active" : "";
  };
  return (
    <>
      <div className="sidebar" data-color={background} data-image={image}>
        <div className="sidebar-wrapper d-flex flex-column justify-content-between">
          <div>
            <div className="logo my-0 pl-2 py-3 pr-3">
              <Link
                to="/dashboard"
                className="simple-text logo-normal m-2 flex-fill p-0"
                aria-label="Watch My Page – go to dashboard"
              >
                <img
                  src={require("assets/img/wmp-logo-01.png")}
                  alt=""
                  className="img-fluid"
                />
              </Link>
            </div>
            <Nav as="ul">
              {createLinks(routes)}
            </Nav>
            {isAdmin && (
              <>
                <hr className="sidebar-divider text-light" style={{ opacity: 0.5 }} />
                <Nav as="ul">
                  <Nav.Item className={activeRoute("/admindata")} as="li">
                    <Nav.Link
                      to="/admindata"
                      as={Link}
                      aria-current={location.pathname === "/admindata" ? "page" : undefined}
                    >
                      <i className="fa-thin fa-chart-mixed" aria-hidden="true" />
                      <p>Admin Data</p>
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item as="li">
                    <Nav.Link
                      as={Link}
                      to={debugActive ? "/dashboard" : "/dashboard?debug"}
                      className="text-light"
                    >
                      <i className="fa-thin fa-bug" />
                      <p>Debug {debugActive ? "ON" : "OFF"}</p>
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
              </>
            )}
          </div>
          <div className="sidebar-footer">
            <div className="text-center m-4">
              <button
                type="button"
                className="sidebar-footer__link text-light border-0 bg-transparent p-0"
                onClick={(e) => {
                  e.preventDefault();
                  logout({ returnTo: window.location.origin });
                }}
                aria-label="Sign out"
              >
                <i className="fal fa-sign-out-alt" aria-hidden="true" /> Sign Out
              </button>
            </div>
            <div className="text-center mb-0">
              <a
                className="sidebar-footer__link text-light"
                href="mailto:support@watchmypage.com"
                aria-label="Help and support"
              >
                <i className="fal fa-life-ring" aria-hidden="true" /> Help and Support
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

let linkPropTypes = {
  path: PropTypes.string,
  layout: PropTypes.string,
  name: PropTypes.string,
  component: PropTypes.oneOfType([PropTypes.func, PropTypes.element])
};

Sidebar.defaultProps = {
  image: "",
  background: "black",
  routes: []
};

Sidebar.propTypes = {
  image: PropTypes.string,
  background: PropTypes.oneOf([
    "black",
    "azure",
    "green",
    "orange",
    "red",
    "purple"
  ]),
  routes: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        ...linkPropTypes,
        icon: PropTypes.string
      }),
      PropTypes.shape({
        collapse: true,
        path: PropTypes.string,
        name: PropTypes.string,
        state: PropTypes.string,
        icon: PropTypes.string,
        views: PropTypes.shape({
          ...linkPropTypes,
          mini: PropTypes.string
        })
      })
    ])
  )
};

export default Sidebar;
